import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useGoogleLogin } from "@react-oauth/google";
import { useAuthStore } from "../../stores/auth.store";
import { useResources } from "../../hooks/useResources";
import type { AuthError } from "../../interfaces/apiInterfaces/auth";
import { globalConfig } from "../../config/globalConfig";
import googleIcon from "../../assets/images/google_icon.svg";
import nagwaLogo from "../../assets/images/nagwa-logo.svg";
import "./SignIn.scss";

const SignIn: React.FC = () => {
  const navigate = useNavigate();
  const { loginWithGoogle } = useAuthStore();
  const { getResourceValue } = useResources();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const googleLogin = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      setIsLoading(true);
      setError(null);

      try {
        // Fetch user info from Google using the access token
        const userInfoResponse = await fetch(
          globalConfig.googleUserInfoUrl,
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );

        if (!userInfoResponse.ok) {
          throw new Error("Failed to fetch user info from Google");
        }

        const userInfo = await userInfoResponse.json();
        const email = userInfo.email;

        // Validate user through auth store (domain + whitelist check)
        await loginWithGoogle(email);

        // If successful, navigate to home
        navigate("/");
      } catch (err) {
        const authError = err as AuthError;
        setError(authError.message || "Authentication failed. Please try again.");
      } finally {
        setIsLoading(false);
      }
    },
    onError: () => {
      setError("Google authentication failed. Please try again.");
    },
  });

  const handleGoogleSignIn = () => {
    setError(null);
    googleLogin();
  };

  return (
    <div className="sign-in page">
      <div className="logo">
        <figure>
          <img src={nagwaLogo} alt="Nagwa Logo" />
        </figure>
      </div>

      <main>
        <div className="page-title">
          <h1>{getResourceValue("welcome_to_qms", "Welcome to Nagwa QMS!")}</h1>
        </div>

        <div className="section">
          <div className="section-content">
            {error && (
              <div className="error-message" style={{ 
                color: '#d32f2f', 
                marginBottom: '16px', 
                textAlign: 'center',
                padding: '12px',
                backgroundColor: '#ffebee',
                borderRadius: '4px',
                fontSize: '14px'
              }}>
                {error}
              </div>
            )}
            
            <div className="social-login">
              <button
                onClick={handleGoogleSignIn}
                className="btn btn-white btn-block big"
                disabled={isLoading}
              >
                <img src={googleIcon} alt="" />
                {isLoading 
                  ? getResourceValue("signing_in", "Signing in...") 
                  : getResourceValue("continue_with_google", "Continue with Google")}
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default SignIn;
